import { useState } from "preact/compat"
import { api_fetch } from "@/api/client.tsx"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { Logo } from "@/app/logo.tsx"
import { useAsyncEffect, usePageTitle, useSearchParams } from "@/utils"
import { DEFAULT_LOGGED_IN_PATH } from "@/app/route.tsx"
import { sanitize_next_for_redirect } from "@/auth/sanitize_next.ts"
import { setUser } from "@/auth/user.tsx"
import { User } from "@/api"

interface OrgOption {
  id: string
  name: string
  vendor_qualifier?: string | null
}

function format_org_display(org: OrgOption): string {
  return org.vendor_qualifier ? `${org.name} (${org.vendor_qualifier})` : org.name
}

function org_matches_search(org: OrgOption, query: string): boolean {
  const needle = query.toLowerCase()
  const hay = `${org.name} ${org.vendor_qualifier ?? ""}`.toLowerCase()
  return hay.includes(needle)
}

export function SelectOrg() {
  usePageTitle("Select Organization")
  const params = useSearchParams<{ next?: string }>()
  const [orgs, setOrgs] = useState<OrgOption[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useAsyncEffect(async () => {
    const res = await api_fetch<OrgOption[]>("/auth/organizations")
    if (res.ok) {
      setOrgs(res.value.data)
    } else {
      setError(res.error.message)
    }
    setLoading(false)
  }, [])

  async function handleSelect(orgId: string) {
    const res = await api_fetch("/auth/select-org", {
      body: { org_id: orgId },
    })
    if (res.ok) {
      // Fetch updated user (with org_id resolved from mw_org cookie) and
      // persist to localStorage before navigating. Without this, the
      // Authenticated guard reads stale state and redirects back here.
      const me = await api_fetch<User>("/auth/me")
      if (me.ok) {
        setUser(me.value.data)
      }
      const raw_next = params.next ?? DEFAULT_LOGGED_IN_PATH
      const next =
        raw_next.includes("http://localhost") || raw_next.includes("https://admin.marginwiz.com")
          ? raw_next
          : (sanitize_next_for_redirect(raw_next) ?? DEFAULT_LOGGED_IN_PATH)
      window.location.href = next
    } else {
      setError(res.error?.message ?? "Failed to select organization")
    }
  }

  const filtered = orgs
    .filter((o: OrgOption) => org_matches_search(o, search))
    .sort((a: OrgOption, b: OrgOption) => a.name.localeCompare(b.name))

  if (loading) {
    return (
      <CenterColumnContainer title="Select Organization" logo={<Logo />}>
        <p class="text-gray-500">Loading organizations...</p>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer title="Select Organization" subtitle="Choose which brand to access" logo={<Logo />}>
      {error && <p class="text-red-500 text-sm mb-4">{error}</p>}
      <input
        type="text"
        placeholder="Search organizations..."
        value={search}
        onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
        class="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      />
      <div class="max-h-96 overflow-y-auto space-y-1">
        {filtered.map((org) => (
          <button
            key={org.id}
            onClick={() => handleSelect(org.id)}
            class="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 text-gray-900 transition-colors"
          >
            {format_org_display(org)}
          </button>
        ))}
        {filtered.length === 0 && (
          <p class="text-gray-400 text-sm text-center py-4">No organizations found</p>
        )}
      </div>
    </CenterColumnContainer>
  )
}
